import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormControl, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { PaymentMethodService } from '../../../../services/payment-method.service';
import { BanksService } from '../../../../services/banks.service';
import { ToastService } from "../../../../services/toast.service";
import { LoadingService } from "../../../../services/loading.service";

// 🎯 Interface tipada para el formulario usando FormControls
interface PaymentMethodFormControls {
  name: FormControl<string>;
  isBankTransfer: FormControl<boolean>;
  defaultBankId: FormControl<number | null>;
  isPaymentGateway: FormControl<boolean>;
  gatewayProvider: FormControl<string>;
  isIntegrated: FormControl<boolean>;
  requiresLinkGeneration: FormControl<boolean>;
}

// 🎯 Interface para el valor del formulario
interface PaymentMethodFormValue {
  name: string;
  isBankTransfer: boolean;
  defaultBankId: number | null;
  isPaymentGateway: boolean;
  gatewayProvider: string;
  isIntegrated: boolean;
  requiresLinkGeneration: boolean;
}

// 🎯 Interface para los datos del backend
interface PaymentMethodResponse {
  name: string;
  isBankTransfer: boolean;
  defaultBankId: number | null;
  defaultBank: any | null;
  isPaymentGateway: boolean;
  gatewayProvider: string | null;
  isIntegrated: boolean;
  requiresLinkGeneration: boolean;
  id: number;
  createdAt: string | null;
  updatedAt: string | null;
  deletedAt: string | null;
  deletedBy: string | null;
  isDeleted: boolean;
}

@Component({
  selector: 'app-new-payment-method',
  templateUrl: './new-payment-method.component.html',
  styleUrls: ['./new-payment-method.component.scss']
})
export class NewPaymentMethodComponent implements OnInit {
  // 🎯 FormGroup tipado usando la interface de FormControls
  paymentMethodForm!: FormGroup<PaymentMethodFormControls>;
  
  // 🎯 Estado del componente
  readonly isEditing = this.route.snapshot.paramMap.has('id');
  readonly paymentMethodId = this.route.snapshot.paramMap.get('id');
  submitting = false;
  banksList: any[] = [];

  constructor(
    private fb: FormBuilder,
    private paymentMethodService: PaymentMethodService,
    private banksService: BanksService,
    private toastService: ToastService,
    public router: Router,
    private route: ActivatedRoute,
    private loadingService: LoadingService
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    // 🚀 Cargar lista de bancos
    this.loadBanks();
    
    // 🚀 Cargar datos si estamos en modo edición
    if (this.isEditing && this.paymentMethodId) {
      this.loadPaymentMethod();
    }
  }

  /**
   * 🏗️ Inicializa el FormGroup con FormControls tipados
   */
  private initForm(): void {
    this.paymentMethodForm = this.fb.group<PaymentMethodFormControls>({
      name: this.fb.control<string>('', {
        nonNullable: true,
        validators: [Validators.required, Validators.minLength(3), Validators.maxLength(100)]
      }),
      isBankTransfer: this.fb.control<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      defaultBankId: this.fb.control<number | null>(null, {
        nonNullable: true
      }),
      isPaymentGateway: this.fb.control<boolean>(false, {
        nonNullable: true
      }),
      gatewayProvider: this.fb.control<string>('', {
        nonNullable: true
      }),
      isIntegrated: this.fb.control<boolean>(false, {
        nonNullable: true
      }),
      requiresLinkGeneration: this.fb.control<boolean>(false, {
        nonNullable: true
      })
    });

    // 🔗 Configurar validaciones condicionales
    this.setupConditionalValidators();
  }

  /**
   * ⚙️ Configura validadores condicionales basados en el estado del formulario
   */
  private setupConditionalValidators(): void {
    const gatewayProviderControl = this.paymentMethodForm.controls.gatewayProvider;
    const isPaymentGatewayControl = this.paymentMethodForm.controls.isPaymentGateway;
    const defaultBankIdControl = this.paymentMethodForm.controls.defaultBankId;
    const isBankTransferControl = this.paymentMethodForm.controls.isBankTransfer;

    // 🎯 Cuando cambia isPaymentGateway, actualizar validador de gatewayProvider
    isPaymentGatewayControl.valueChanges.subscribe(isGateway => {
      if (isGateway) {
        gatewayProviderControl.setValidators([Validators.required]);
      } else {
        gatewayProviderControl.clearValidators();
        gatewayProviderControl.setValue(''); // Reset value when not needed
      }
      gatewayProviderControl.updateValueAndValidity();
    });

    // 🏦 Cuando cambia isBankTransfer, limpiar el banco si no es transferencia
    isBankTransferControl.valueChanges.subscribe(isBankTransfer => {
      if (!isBankTransfer) {
        defaultBankIdControl.setValue(null);
      }
    });
  }

  /**
   * 🏦 Carga la lista de bancos disponibles
   */
  private loadBanks(): void {
    this.banksService.getAll().subscribe({
      next: (response: any) => {
        // Manejar estructura paginada: data puede ser array directo o {data: [...], totalCount: ...}
        const data = response?.data;
        this.banksList = Array.isArray(data) ? data : (data?.data || []);
      },
      error: (error) => {
        console.error('Error loading banks:', error);
        this.toastService.showToast('error', 'Error al cargar la lista de bancos');
        this.banksList = [];
      }
    });
  }

  /**
   * 📥 Carga los datos del método de pago para edición usando Reactive Forms nativo
   */
  private loadPaymentMethod(): void {
    this.loadingService.show();
    
    this.paymentMethodService.getById(Number(this.paymentMethodId)).subscribe({
      next: ({ data }: { data: PaymentMethodResponse }) => {
        // 🎯 Usar patchValue nativo de Angular Reactive Forms
        // Automáticamente maneja los tipos y actualiza la UI
        this.paymentMethodForm.patchValue({
          name: data.name ?? '',
          isBankTransfer: Boolean(data.isBankTransfer),
          defaultBankId: data.defaultBankId ?? null,
          isPaymentGateway: Boolean(data.isPaymentGateway),
          gatewayProvider: data.gatewayProvider ?? '',
          isIntegrated: Boolean(data.isIntegrated),
          requiresLinkGeneration: Boolean(data.requiresLinkGeneration)
        });
        
        this.loadingService.hide();
      },
      error: (error) => {
        console.error('Error loading payment method:', error);
        this.toastService.showToast('error', 'Error al cargar la información del método de pago');
        this.loadingService.hide();
        this.router.navigate(['/config/payment-methods']);
      }
    });
  }

  /**
   * 📤 Envía el formulario usando Reactive Forms nativo
   */
  onSubmit(): void {
    // 🚦 Validación del formulario
    if (this.paymentMethodForm.invalid) {
      this.paymentMethodForm.markAllAsTouched();
      this.toastService.showToast('error', 'Por favor, complete todos los campos requeridos');
      return;
    }

    if (this.submitting) return; // Prevenir doble envío

    this.submitting = true;
    this.loadingService.show();

    // 🎯 Obtener valores usando getRawValue() (incluye controles disabled)
    const formValue: PaymentMethodFormValue = this.paymentMethodForm.getRawValue();

    // 🔄 Llamada al servicio según el modo (crear/actualizar)
    const serviceCall = this.isEditing && this.paymentMethodId
      ? this.paymentMethodService.update(Number(this.paymentMethodId), formValue)
      : this.paymentMethodService.insert(formValue);

    serviceCall.subscribe({
      next: () => {
        const action = this.isEditing ? 'actualizado' : 'creado';
        this.toastService.showToast('success', `Método de pago ${action} exitosamente`);
        this.router.navigate(['/config/payment-methods']);
      },
      error: (error) => {
        console.error('Error submitting payment method:', error);
        const action = this.isEditing ? 'actualizar' : 'crear';
        this.toastService.showToast('error', `Error al ${action} el método de pago`);
        this.submitting = false;
        this.loadingService.hide();
      },
      complete: () => {
        this.submitting = false;
        this.loadingService.hide();
      }
    });
  }

  /**
   * 🔍 Verifica si un control tiene errores específicos
   */
  hasError(controlName: keyof PaymentMethodFormControls, errorType: string): boolean {
    const control = this.paymentMethodForm.controls[controlName];
    return (control.touched || control.dirty) && control.hasError(errorType);
  }

  /**
   * 🔄 Maneja cambios en el radio button de tipo de procesamiento
   */
  onBankTransferChange(): void {
    const isBankTransfer = this.paymentMethodForm.controls.isBankTransfer.value;
    
    if (!isBankTransfer) {
      // 🧹 Limpiar banco cuando no es transferencia bancaria
      this.paymentMethodForm.patchValue({
        defaultBankId: null
      });
    }
  }

  /**
   * 🔄 Maneja cambios en el checkbox de pasarela de pago
   * Reactivo: se ejecuta automáticamente por el setupConditionalValidators
   */
  onPaymentGatewayChange(): void {
    const isPaymentGateway = this.paymentMethodForm.controls.isPaymentGateway.value;
    
    if (!isPaymentGateway) {
      // 🧹 Limpiar campos relacionados cuando se deshabilita gateway
      this.paymentMethodForm.patchValue({
        gatewayProvider: '',
        isIntegrated: false,
        requiresLinkGeneration: false
      });
    }
  }

  /**
   * 🔄 Maneja cambios en el checkbox de integración
   */
  onIntegratedChange(): void {
    const isIntegrated = this.paymentMethodForm.controls.isIntegrated.value;
    
    if (!isIntegrated) {
      // 🧹 Limpiar generación de link cuando no está integrado
      this.paymentMethodForm.patchValue({
        requiresLinkGeneration: false
      });
    }
  }

  // 🎯 Getters para el template - Usan controles tipados
  
  /**
   * ✅ Indica si los campos de pasarela están habilitados
   */
  get isPaymentGatewayEnabled(): boolean {
    return this.paymentMethodForm.controls.isPaymentGateway.value;
  }

  /**
   * ✅ Indica si los campos de integración están habilitados
   */
  get isIntegratedFieldsEnabled(): boolean {
    return this.isPaymentGatewayEnabled && 
           this.paymentMethodForm.controls.isIntegrated.value;
  }

  /**
   * 🏪 Lista de proveedores de pasarela disponibles
   */
  readonly gatewayProviders = [
    { label: 'Wompi', value: 'wompi' },
    { label: 'PlacetoPay', value: 'placetopay' },
    { label: 'Otro', value: 'other' }
  ] as const;
}
